<template>
  <div id="sponsorSectionEdit">
    <app-menu></app-menu>
    <div class="col-lg-10 offset-lg-1 pb-5">
      <h3 class="text-center pb-3 pt-3">{{ section.id ? "Upravit sekci" : "Nová sekce" }}</h3>

      <!-- Nastavení sekce -->
      <div class="card card-body mb-4">
        <div class="form-row">
          <div class="form-group col-lg-6">
            <label>Režim zobrazení:</label>
            <select class="form-control" v-model="section.displayMode">
              <option value="GROUP">Skupina – jeden nadpis nad logy</option>
              <option value="INDIVIDUAL">Individuální – každé logo má vlastní nadpis</option>
            </select>
          </div>
          <div class="form-group col-lg-6">
            <label>Počet log v řádku:</label>
            <select class="form-control" v-model="section.colClass">
              <option value="col-lg-3">4 v řádku</option>
              <option value="col-lg-4">3 v řádku</option>
              <option value="col-lg-6">2 v řádku</option>
              <option value="col-lg-12">1 v řádku</option>
            </select>
          </div>
        </div>
        <div class="row" v-if="section.displayMode !== 'INDIVIDUAL'">
          <div class="form-group col-lg-6">
            <label>Nadpis sekce (CZ):</label>
            <input class="form-control" v-model="section.title.cs" />
          </div>
          <div class="form-group col-lg-6">
            <label>Nadpis sekce (EN):</label>
            <input class="form-control" v-model="section.title.en" />
          </div>
        </div>
        <p v-else class="text-muted mb-2">
          V individuálním režimu má vlastní nadpis každé logo (viz níže).
        </p>
        <div class="form-check">
          <input id="secvis" type="checkbox" class="form-check-input" v-model="section.visible" />
          <label for="secvis" class="form-check-label">Viditelná na stránce</label>
        </div>
        <div class="text-center pt-3">
          <send-button ref="secBtn" :send="saveSection" text="Uložit sekci" :response="error" />
        </div>
      </div>

      <!-- Loga -->
      <template v-if="section.id">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h4 class="mb-0">Loga</h4>
          <button class="btn btn-success" @click="addLogo">Přidat logo</button>
        </div>
        <p class="text-muted">Pořadí log měňte přetažením za úchyt.</p>

        <draggable v-model="section.logos" handle=".drag-handle" @change="saveLogoOrder">
          <div class="card card-body logo-card mb-3" v-for="logo in section.logos" :key="logo.key">
            <div class="row">
              <div class="col-lg-3 text-center">
                <i class="bi-list drag-handle" v-if="logo.id"></i>
                <div class="logo-preview">
                  <img v-if="logoPreview(logo)" :src="logoPreview(logo)" alt="náhled loga" />
                  <span v-else class="text-muted">bez obrázku</span>
                </div>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml"
                  class="form-control-file mt-2"
                  @change="(e) => selectImage(e, logo)"
                />
              </div>
              <div class="col-lg-9">
                <div class="form-row">
                  <div class="form-group col-lg-8">
                    <label>Popisek / alt text:</label>
                    <input class="form-control" v-model="logo.alt" />
                  </div>
                  <div class="form-group col-lg-4">
                    <label>Šířka (%):</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      class="form-control"
                      v-model.number="logo.widthPercent"
                      placeholder="auto"
                    />
                  </div>
                </div>
                <div class="form-group">
                  <label>Odkaz (URL):</label>
                  <input class="form-control" v-model="logo.url" placeholder="prázdné = bez odkazu" />
                </div>
                <div class="form-row" v-if="section.displayMode === 'INDIVIDUAL'">
                  <div class="form-group col-lg-6">
                    <label>Nadpis loga (CZ):</label>
                    <input class="form-control" v-model="logo.title.cs" />
                  </div>
                  <div class="form-group col-lg-6">
                    <label>Nadpis loga (EN):</label>
                    <input class="form-control" v-model="logo.title.en" />
                  </div>
                </div>
                <div class="d-flex align-items-center justify-content-between">
                  <div class="form-check">
                    <input
                      :id="'logovis-' + logo.key"
                      type="checkbox"
                      class="form-check-input"
                      v-model="logo.visible"
                    />
                    <label :for="'logovis-' + logo.key" class="form-check-label">Viditelné</label>
                  </div>
                  <div>
                    <button class="btn btn-outline-danger btn-sm mr-2" @click="deleteLogo(logo)">
                      Smazat
                    </button>
                    <button class="btn btn-primary btn-sm" :disabled="logo.saving" @click="saveLogo(logo)">
                      <span v-if="logo.saving" class="spinner-border spinner-border-sm" role="status"></span>
                      Uložit logo
                    </button>
                  </div>
                </div>
                <small v-if="logo.savedMessage" class="text-success">{{ logo.savedMessage }}</small>
              </div>
            </div>
          </div>
        </draggable>
      </template>
      <p v-else class="text-muted">Loga lze přidávat po prvním uložení sekce.</p>

      <div class="text-center pt-4">
        <router-link to="/admin/sponsors" class="btn btn-secondary">Zpět na přehled</router-link>
      </div>
    </div>
    <error-modal :message="error" />
  </div>
</template>

<script>
import AdminMenu from "@/components/admin/AdminMenu";
import axios from "@/api/axiom-instance";
import config from "@/config";
import ErrorModal from "@/components/admin/ErrorModal";
import SendButton from "@/components/admin/SendButton";
import draggable from "vuedraggable";

let keyCounter = 0;

export default {
  name: "SponsorSectionEdit",
  components: {
    "app-menu": AdminMenu,
    "error-modal": ErrorModal,
    "send-button": SendButton,
    draggable: draggable,
  },
  data() {
    return {
      section: {
        id: null,
        title: { cs: "", en: "" },
        displayMode: "GROUP",
        colClass: "col-lg-4",
        order: null,
        visible: true,
        logos: [],
      },
      error: null,
    };
  },
  created() {
    if (this.$route.params.id != null) {
      this.load(this.$route.params.id);
    }
  },
  methods: {
    load: function (id) {
      axios
        .get("/sponsors/section", { params: { id: id } })
        .then((success) => {
          this.section = this.normalizeSection(success.data);
        })
        .catch((error) => (this.error = error));
    },
    normalizeSection: function (data) {
      return {
        id: data.id,
        title: data.title || { cs: "", en: "" },
        displayMode: data.displayMode || "GROUP",
        colClass: data.colClass || "col-lg-4",
        order: data.order,
        visible: data.visible,
        logos: (data.logos || []).map((l) => this.normalizeLogo(l)),
      };
    },
    normalizeLogo: function (l) {
      return {
        key: ++keyCounter,
        id: l.id,
        sectionId: l.sectionId,
        title: l.title || { cs: "", en: "" },
        alt: l.alt || "",
        url: l.url || "",
        widthPercent: l.widthPercent,
        imageExt: l.imageExt,
        updated: l.updated || 0,
        visible: l.visible,
        image: null,
        saving: false,
        savedMessage: null,
      };
    },
    saveSection: function () {
      const dto = {
        id: this.section.id,
        title: this.section.title,
        displayMode: this.section.displayMode,
        colClass: this.section.colClass,
        order: this.section.order,
        visible: this.section.visible,
      };
      axios
        .post("/sponsors/section", dto)
        .then((success) => {
          const isNew = this.section.id == null;
          this.section.id = success.data.id;
          this.section.order = success.data.order;
          this.$refs.secBtn.enable();
          if (isNew) {
            this.$router.replace({ path: "/admin/sponsors/section/edit/" + success.data.id });
          }
        })
        .catch((error) => {
          this.error = error;
          this.$refs.secBtn.enable();
        });
    },
    addLogo: function () {
      this.section.logos.push({
        key: ++keyCounter,
        id: null,
        sectionId: this.section.id,
        title: { cs: "", en: "" },
        alt: "",
        url: "",
        widthPercent: null,
        imageExt: null,
        updated: 0,
        visible: true,
        image: null,
        saving: false,
        savedMessage: null,
      });
    },
    selectImage: function (e, logo) {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (evt) => {
        logo.image = evt.target.result;
        logo.savedMessage = null;
      };
      reader.readAsDataURL(file);
    },
    logoPreview: function (logo) {
      if (logo.image) return logo.image;
      if (logo.id && logo.imageExt) {
        return config.API_URL + "/sponsor-image?id=" + logo.id + "&v=" + (logo.updated || 0);
      }
      return null;
    },
    saveLogo: function (logo) {
      logo.saving = true;
      logo.savedMessage = null;
      const dto = {
        id: logo.id,
        sectionId: this.section.id,
        title: logo.title,
        alt: logo.alt,
        url: logo.url ? logo.url : null,
        widthPercent: logo.widthPercent != null && logo.widthPercent !== "" ? logo.widthPercent : null,
        visible: logo.visible,
        image: logo.image,
      };
      axios
        .post("/sponsors/logo", dto)
        .then((success) => {
          logo.id = success.data.id;
          logo.imageExt = success.data.imageExt;
          logo.updated = success.data.updated;
          logo.image = null;
          logo.saving = false;
          logo.savedMessage = "Uloženo.";
        })
        .catch((error) => {
          logo.saving = false;
          this.error = error;
        });
    },
    deleteLogo: function (logo) {
      if (logo.id == null) {
        this.section.logos = this.section.logos.filter((l) => l.key !== logo.key);
        return;
      }
      axios
        .post("/sponsors/logo/delete", null, { params: { id: logo.id } })
        .then(() => {
          this.section.logos = this.section.logos.filter((l) => l.key !== logo.key);
        })
        .catch((error) => (this.error = error));
    },
    saveLogoOrder: function () {
      const ids = this.section.logos.filter((l) => l.id != null).map((l) => l.id);
      if (ids.length === 0) return;
      axios
        .post("/sponsors/logo/order", ids)
        .catch((error) => (this.error = error));
    },
  },
};
</script>

<style scoped>
#sponsorSectionEdit {
  min-height: 100vh;
  padding-bottom: 4em;
  overflow: hidden;
}

.drag-handle {
  cursor: move;
  font-size: 1.4rem;
}

.logo-preview {
  min-height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 8px;
}

.logo-preview img {
  max-width: 100%;
  max-height: 120px;
  object-fit: contain;
}
</style>
